export function LicensingRequirements() {
  const steps = [
    {
      number: '01',
      title: 'Property Assessment',
      duration: '1-2 weeks',
      description: 'Verify zoning compliance, strata permissions, and property eligibility for STR operations in Surrey.'
    },
    {
      number: '02',
      title: 'Documentation Preparation',
      duration: '1-2 weeks',
      description: 'Gather required documents including proof of ownership, insurance, safety certifications, and strata approval if applicable.'
    },
    {
      number: '03',
      title: 'Business License Application',
      duration: '2-4 weeks',
      description: 'Submit home-based business license application to City of Surrey with all supporting documentation and fees.'
    },
    {
      number: '04',
      title: 'Safety Compliance',
      duration: '1-2 weeks',
      description: 'Ensure property meets all fire safety, building code, and accessibility requirements with proper documentation.'
    },
    {
      number: '05',
      title: 'Platform Registration',
      duration: '1 week',
      description: 'Register with rental platforms, display license number, and set up tax collection for MRDT and PST.'
    }
  ];

  const requiredDocuments = [
    'Proof of property ownership (title or deed)',
    'Valid property insurance covering short-term rentals',
    'Strata approval letter (if applicable)',
    'Floor plan showing emergency exits',
    'Fire safety equipment certification',
    'Smoke and CO detector installation confirmation',
    'Parking availability documentation',
    'Waste management plan',
    'Good neighbor agreement or contact protocol',
    'Tax registration numbers (GST/HST if applicable)'
  ];

  const fees = [
    { item: 'Business License Application', cost: '$150 - $300' },
    { item: 'Annual License Renewal', cost: '$100 - $200' },
    { item: 'Fire Safety Inspection (if required)', cost: '$200 - $400' },
    { item: 'Zoning Confirmation Letter', cost: '$50 - $100' }
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Licensing Requirements & Process
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Complete guide to obtaining your Surrey STR business license and maintaining compliance with city requirements.
          </p>
        </div>

        {/* Process Timeline */}
        <div className="mb-20">
          <h3 className="text-2xl font-semibold text-[#505A5A] mb-10 text-center">
            5-Step Licensing Process
            <span className="block text-base font-normal text-[#505A5A]/70 mt-2">
              Total Timeline: 6-10 weeks
            </span>
          </h3>
          <div className="space-y-6">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="bg-[#F0F0F0] rounded-2xl p-8 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-20 h-20 bg-[#882629] rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-2xl">{step.number}</span>
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-xl font-semibold text-[#505A5A]">{step.title}</h4>
                      <span className="text-sm font-medium text-[#882629] bg-white px-4 py-2 rounded-full whitespace-nowrap">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-[#505A5A]/70 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Required Documents */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-[#F0F0F0] rounded-2xl p-8">
            <h3 className="text-2xl font-semibold text-[#505A5A] mb-6 flex items-center">
              <i className="ri-file-list-3-line text-[#882629] text-3xl mr-3"></i>
              Required Documentation
            </h3>
            <ul className="space-y-3">
              {requiredDocuments.map((doc, index) => (
                <li key={index} className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-[#882629] text-lg mr-3 flex-shrink-0 mt-1"></i>
                  <span className="text-[#505A5A]/80">{doc}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#F0F0F0] rounded-2xl p-8">
            <h3 className="text-2xl font-semibold text-[#505A5A] mb-6 flex items-center">
              <i className="ri-money-dollar-circle-line text-[#882629] text-3xl mr-3"></i>
              Licensing Fees
            </h3>
            <div className="space-y-4">
              {fees.map((fee, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between bg-white rounded-xl p-4"
                >
                  <span className="text-[#505A5A] font-medium">{fee.item}</span>
                  <span className="text-[#882629] font-semibold whitespace-nowrap ml-4">{fee.cost}</span>
                </div>
              ))}
              <div className="mt-6 pt-6 border-t-2 border-[#505A5A]/20">
                <div className="flex items-center justify-between">
                  <span className="text-[#505A5A] font-semibold text-lg">Estimated Total (First Year)</span>
                  <span className="text-[#882629] font-bold text-xl">$500 - $1,000</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center bg-[#882629] rounded-2xl p-12 text-white">
          <h3 className="text-3xl font-bold mb-4">Need Help with Surrey Licensing?</h3>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Our team specializes in Surrey STR licensing and can handle the entire process for you, ensuring compliance and faster approval.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-[#882629] px-10 py-5 rounded-full text-lg font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-xl"
          >
            Get Licensing Assistance
          </button>
        </div>
      </div>
    </section>
  );
}
